import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { listTemplates, rollTemplateShareId, upsertTemplate } from '@/src/api/template'
import { Template } from '@/src/types/template.zod'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { Badge } from '@/src/components/ui/badge'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { useTranslation } from '@/src/i18n/client'
import { useAuth } from '@/src/components/AuthContextProvider'

export const Route = createFileRoute('/_manage/manage/admin/templates')({
  component: AdminTemplatesPage,
})

function AdminTemplatesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['template', 'list', 'all'],
    queryFn: () => listTemplates({ data: { all: true } }),
  })

  const rollShareIdMutation = useMutation({
    mutationFn: (templateId: string) => rollTemplateShareId({ data: { templateId } }),
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ template, enabled }: { template: Template; enabled: boolean }) =>
      upsertTemplate({
        data: {
          id: template.id,
          name: template.name,
          description: template.description ?? null,
          content: template.content,
          undefinedValue: template.undefinedValue ?? null,
          type: template.type,
          visibility: template.visibility,
          usage: template.usage,
          enabled,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template', 'list', 'all'] })
    },
  })

  const columns: ColumnConfig<Template>[] = [
    {
      key: 'shareId',
      label: t('table.shareId'),
      width: 'w-24',
      render: (template) => (
        <ShareLinkCell
          shareId={template.shareId}
          reserveActionSpace={true}
          canRotate={isAdmin}
          onRotate={
            isAdmin
              ? async () => {
                  await rollShareIdMutation.mutateAsync(template.id)
                  await queryClient.invalidateQueries({ queryKey: ['template', 'list', 'all'] })
                }
              : undefined
          }
        />
      ),
      copyable: true,
    },
    {
      key: 'name',
      label: t('table.name'),
      render: (template) => template.name,
    },
    {
      key: 'description',
      label: t('table.description'),
      render: (template) => template.description || '-',
    },
    {
      key: 'visibility',
      label: t('table.visibility'),
      render: (template) => (
        <Badge
          variant={
            template.visibility === "PUBLIC"
              ? "default"
              : template.visibility === "ORGANIZATION"
                ? "secondary"
                : "outline"
          }
          className={
            template.visibility === "PUBLIC"
              ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-green-100 text-green-800 border-green-200"
              : template.visibility === "ORGANIZATION"
                ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-blue-100 text-blue-800 border-blue-200"
                : "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-gray-100 text-gray-800 border-gray-200"
          }
        >
          {template.visibility}
        </Badge>
      ),
    },
    {
      key: 'enabled',
      label: t('table.status'),
      align: 'right',
      render: (template) => (
        <EnabledAction
          enabled={!!template.enabled}
          onToggle={async (enabled) => {
            await toggleEnabledMutation.mutateAsync({ template, enabled })
          }}
        />
      ),
    },
  ];

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction
            to="/manage/templates/$id"
            params={{ id: 'new' }}
            label={t('table.createTemplate')}
          />
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="mt-4">
        <DataTable<Template>
          data={(templates as any) ?? []}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('table.noTemplates')}
          actions={(template) => (
            <EditAction
              to="/manage/templates/$id"
              params={{ id: template.id }}
            />
          )}
        />
      </div>
    </div>
  )
}
